import {
    AnalyticsEvent,
    CHATBOT_SUBSCRIBE,
    CHATBOT_UNSUBSCRIBE,
    CHATBOT_WEBHOOK,
    PostAnalyticsFN,
} from '../../common/Analytics';
import { ErrorReporter } from '../../common/ErrorReporter';
import type { ChatbotMetainfo, default as DB } from '../../db/DB';
import { ReplicantError } from '../../Errors';
import type { LineWebhookEvent } from '../../ReplicantChatbot';
import type { Replicant, ReplicantConfig } from '../../ReplicantConfig';
import type { APIAccessInterface, PushDocsToIndexFn } from '../ServerReplicant';
import { executeOnWebhook } from './executeOnWebhook';

export class LineWebhookHandler<T extends Replicant> {
    constructor(
        private opts: {
            apiAccess: APIAccessInterface<T>;
            config: ReplicantConfig<T>;
            dateNow: () => number;
            db: DB<T['state']>;
            errorReporter: ErrorReporter;
            pushDocsToIndex: PushDocsToIndexFn;
            sendAnalyticsEvents: PostAnalyticsFN;
        },
    ) {}

    async handleEvents(webhookEvents: LineWebhookEvent[]): Promise<void> {
        const eventsPerReceiver = groupEventsByReceiver(webhookEvents.filter((e) => !e.deliveryContext.isRedelivery));

        const newIndexDocs: { [docId: string]: Record<string, unknown> } = {};

        // For each receiver, handle received events synchronously in received order:
        const receiverPromises = Object.entries(eventsPerReceiver).map(async ([receiverId, receiverEvents]) => {
            try {
                let metainfo: ChatbotMetainfo | null = null;

                // Update chatbot subscription status on subscription and message events:
                for (const event of receiverEvents) {
                    if (event.type === 'follow') {
                        await Promise.all([
                            this.opts.sendAnalyticsEvents([generateFollowAnalyticEvent(event)]),

                            this.opts.db.chatbot.markSubscribed(event.source.userId, true, this.opts.dateNow()),
                        ]);
                    } else if (event.type === 'unfollow') {
                        if (!metainfo) {
                            metainfo = await this.opts.db.chatbot.fetchMetainfo(event.source.userId);
                        }

                        const analyticsPromise = this.opts.sendAnalyticsEvents([
                            generateUnfollowAnalyticEvent(event, metainfo?.lastReceivedMessagePayload),
                        ]);

                        metainfo = await this.opts.db.chatbot.markSubscribed(
                            event.source.userId,
                            false,
                            this.opts.dateNow(),
                        );

                        await analyticsPromise;
                    } else if (event.type === 'message' && !metainfo?.subscribed) {
                        metainfo = await this.opts.db.chatbot.markSubscribed(receiverId, true, this.opts.dateNow());
                    }
                }

                // Execute `onWebhook` callback for each enabled webhook type:
                const { enabledWebhookEvents, onWebhook } = this.opts.config.chatbot?.events ?? {};
                const onWebhookEvents = receiverEvents.filter((event) => enabledWebhookEvents?.includes(event.type));
                const hasOnWebhookEventsToExecute = onWebhook && onWebhookEvents.length > 0;

                if (hasOnWebhookEventsToExecute) {
                    const analyticsEvents = onWebhookEvents.map((event) => generateWebhookAnalyticEvent(event));

                    await this.opts.sendAnalyticsEvents(analyticsEvents);

                    const { newIndexDoc } = await executeOnWebhook(
                        receiverId,
                        onWebhookEvents,
                        this.opts.apiAccess,
                        this.opts.db,
                    );

                    if (newIndexDoc) {
                        newIndexDocs[receiverId] = newIndexDoc;
                    }
                }
            } catch (error: any) {
                const replicantError = new ReplicantError(
                    'LINE webhook event handler failed: ' + error.message,
                    'server_error',
                );
                replicantError.extras = { events: receiverEvents, reason: error };
                replicantError.stack = error.stack;

                await this.opts.errorReporter.captureException(replicantError);
            }
        });

        await Promise.all(receiverPromises);

        await this.opts.pushDocsToIndex(newIndexDocs);
    }
}

function groupEventsByReceiver(events: LineWebhookEvent[]): { [receiverId: string]: LineWebhookEvent[] } {
    const result: { [receiverId: string]: LineWebhookEvent[] } = {};

    events
        .filter((e) => {
            // Handle all non-subscription events:
            if (!['follow', 'unfollow'].includes(e.type)) {
                return true;
            }

            // Ignore all subscription events apart from each user's latest one: a `follow` event for example overrides any earlier `unfollow` events.
            return !events.some(
                ({ source, timestamp, type }) =>
                    ['follow', 'unfollow'].includes(type) &&
                    source.userId === e.source.userId &&
                    timestamp > e.timestamp,
            );
        })
        .forEach((event) => {
            if (!result[event.source.userId]) {
                result[event.source.userId] = [];
            }

            result[event.source.userId]!.push(event);
        });

    return result;
}

function generateFollowAnalyticEvent(event: LineWebhookEvent): AnalyticsEvent {
    return {
        user_id: event.source.userId,
        event_type: CHATBOT_SUBSCRIBE,
        event_properties: {
            isUnblocked: event.follow?.isUnblocked,
            timestampFromLine: event.timestamp,
        },
    };
}

function generateUnfollowAnalyticEvent(
    event: LineWebhookEvent,
    lastReceivedMessagePayload?: {
        [key: string]: unknown;
        timestamp: number;
    },
): AnalyticsEvent {
    const unfollowEvent: AnalyticsEvent = {
        user_id: event.source.userId,
        event_type: CHATBOT_UNSUBSCRIBE,
        event_properties: { timestampFromLine: event.timestamp },
    };

    if (lastReceivedMessagePayload) {
        const latestChatbotTimeSinceLastReceivedMessage = event.timestamp - lastReceivedMessagePayload.timestamp;

        unfollowEvent.event_properties = {
            ...unfollowEvent.event_properties,
            latestChatbotReceivedMessagePayload: lastReceivedMessagePayload,
            latestChatbotTimeSinceLastReceivedMessage,
        };
    }

    return unfollowEvent;
}

function generateWebhookAnalyticEvent(event: LineWebhookEvent): AnalyticsEvent {
    return {
        user_id: event.source.userId,
        event_type: CHATBOT_WEBHOOK,
        event_properties: { ...event, timestampFromLine: event.timestamp },
    };
}
